import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Portal,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
    Chip,
    IconButton,
    Tooltip,
    TextField,
    InputAdornment,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import { useHamburgerMenu } from '../../../Contexts/HamburgerMenuContext.js';
import modalStyles from '../../JournalEntryies/SubComponents/JournalEntryTemplatesModal.module.scss';

const CHECK_TEXT = {
    PalcodePlacement: { title: 'PalcodePlacementCheck', empty: 'PalcodePlacementNoErrors' },
    MissingPalCode: { title: 'MissingPalCodeCheck', empty: 'MissingPalCodeNoErrors' },
    WithholdingCardPaymentItem: { title: 'WithholdingCardPaymentItemCheck', empty: 'WithholdingCardPaymentItemNoErrors' },
};

const accountLabel = (name, id) => name || id || '';

// Reason texts are "<problem> – <explanation>"; the chip shows only the problem part.
const shortReason = (text) => (text || '').split(/\s[–—]\s/)[0];

const vatLabel = (vat, t) => (vat === 16 ? t('AccountingTax') : `${vat ?? 0}%`);

const PalcodePlacementCheckDialog = ({
    open,
    onClose,
    onRefresh,
    violations = [],
    checkType = 'PalcodePlacement',
    direction = 'rtl',
}) => {
    const { t } = useTranslation();
    const { handleDocumentRowClick, isJustEditModalOpen } = useHamburgerMenu();

    const isRtl = direction === 'rtl';
    const textAlign = isRtl ? 'right' : 'left';
    const text = CHECK_TEXT[checkType] || CHECK_TEXT.PalcodePlacement;

    const [search, setSearch] = useState('');
    const [reasonFilter, setReasonFilter] = useState([]);
    const [docTypeFilter, setDocTypeFilter] = useState([]);
    const [sort, setSort] = useState({ key: null, dir: 'asc' });
    const [refreshing, setRefreshing] = useState(false);

    useEffect(() => {
        setSearch('');
        setReasonFilter([]);
        setDocTypeFilter([]);
        setSort({ key: null, dir: 'asc' });
    }, [checkType, open]);

    const handleRefresh = async () => {
        if (!onRefresh || refreshing) return;
        setRefreshing(true);
        try {
            await onRefresh();
        } finally {
            setRefreshing(false);
        }
    };

    // Rows edited from this dialog are re-checked once the EditModal closes.
    const prevEditOpenRef = useRef(isJustEditModalOpen);
    useEffect(() => {
        if (prevEditOpenRef.current && !isJustEditModalOpen && open) handleRefresh();
        prevEditOpenRef.current = isJustEditModalOpen;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isJustEditModalOpen, open]);

    const rows = useMemo(
        () => (violations || []).filter((v) => (v.checkType || 'PalcodePlacement') === checkType),
        [violations, checkType]
    );

    const countBy = (key) => rows.reduce((acc, r) => {
        const k = r[key] || '';
        acc[k] = (acc[k] || 0) + 1;
        return acc;
    }, {});
    const reasonCounts = useMemo(() => countBy('violationReason'), [rows]); // eslint-disable-line react-hooks/exhaustive-deps
    const docTypeCounts = useMemo(() => countBy('documentType'), [rows]); // eslint-disable-line react-hooks/exhaustive-deps

    const sortValue = useMemo(() => ({
        monthYear: (r) => (r.date ? new Date(r.date).getTime() : 0),
        documentType: (r) => t(r.documentType || ''),
        description: (r) => r.description || '',
        reference: (r) => r.reference || '',
        amount: (r) => Number(r.amount) || 0,
        vat: (r) => Number(r.vat) || 0,
        sortCodeName: (r) => r.sortCodeName || '',
        debitAccount: (r) => accountLabel(r.debitAccountName, r.debitAccount),
        creditAccount: (r) => accountLabel(r.creditAccountName, r.creditAccount),
        violationReason: (r) => t(r.violationReason || ''),
    }), [t]);

    const visibleRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        const filtered = rows.filter((r) => {
            if (reasonFilter.length && !reasonFilter.includes(r.violationReason || '')) return false;
            if (docTypeFilter.length && !docTypeFilter.includes(r.documentType || '')) return false;
            if (!q) return true;
            return [
                r.monthYear, t(r.documentType || ''), r.description, r.reference,
                r.amount, beautifyNumber(r.amount), r.sortCodeName,
                r.debitAccount, r.debitAccountName, r.debitPalCodeType, r.debitPalCode,
                r.creditAccount, r.creditAccountName, r.creditPalCodeType, r.creditPalCode,
                t(r.violationReason || ''),
            ].some((v) => v != null && String(v).toLowerCase().includes(q));
        });
        if (!sort.key) return filtered;
        const get = sortValue[sort.key];
        const factor = sort.dir === 'asc' ? 1 : -1;
        return [...filtered].sort((a, b) => {
            const va = get(a);
            const vb = get(b);
            const cmp = typeof va === 'number' && typeof vb === 'number'
                ? va - vb
                : String(va).localeCompare(String(vb), 'he', { numeric: true, sensitivity: 'base' });
            return cmp * factor;
        });
    }, [rows, search, reasonFilter, docTypeFilter, sort, sortValue, t]);

    const toggle = (setter) => (value) =>
        setter((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
    const toggleReason = toggle(setReasonFilter);
    const toggleDocType = toggle(setDocTypeFilter);

    const handleSort = (key) =>
        setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

    const hasFilters = search || reasonFilter.length || docTypeFilter.length || sort.key;
    const clearFilters = () => {
        setSearch('');
        setReasonFilter([]);
        setDocTypeFilter([]);
        setSort({ key: null, dir: 'asc' });
    };

    // Opens the shared EditModal for the clicked transaction (same mechanism as VatModal /
    // UnprocessedItemsModal). documentType already matches the section keys the edit flow expects
    // ("Incomes" | "Expenses" | "JournalEntries" | "PaymentAcceptances").
    const handleRowClick = (row) => {
        if (!row?.documentId || !row?.documentType) return;
        if (handleDocumentRowClick) {
            handleDocumentRowClick(row.documentId, row.documentType);
        }
    };

    // Highlights the balance-sheet (מאזני / מאזני רכוש קבוע) vs P&L (תוצאתי) palcode type.
    const renderType = (type, missingPalCode, palCode) => {
        if (missingPalCode) {
            return (
                <Chip
                    label={palCode ? `${t('UnknownPalCodeShort')} ${palCode}` : t('NoPalCode')}
                    size="small"
                    sx={{ height: '20px', fontSize: '0.7rem', backgroundColor: '#fff3e0', color: '#e65100' }}
                />
            );
        }
        if (!type) return <span style={{ color: '#999' }}>-</span>;
        const isResult = type === 'תוצאתי';
        return (
            <Chip
                label={type}
                size="small"
                sx={{
                    height: '20px',
                    fontSize: '0.7rem',
                    backgroundColor: isResult ? '#ffe0e0' : '#e0f0ff',
                    color: isResult ? '#b71c1c' : '#0d47a1',
                }}
            />
        );
    };

    const filterChip = (key, label, count, selected, onClick, tooltip) => (
        <Tooltip key={key} title={tooltip || ''} disableHoverListener={!tooltip}>
            <Chip
                label={`${label} (${count})`}
                size="small"
                clickable
                onClick={onClick}
                color={selected ? 'primary' : 'default'}
                variant={selected ? 'filled' : 'outlined'}
                sx={{ maxWidth: 380, fontSize: '0.75rem' }}
            />
        </Tooltip>
    );

    const columns = [
        { key: 'monthYear', label: 'MonthYear' },
        { key: 'documentType', label: 'Type' },
        { key: 'description', label: 'Description' },
        { key: 'reference', label: 'Reference' },
        { key: 'amount', label: 'Amount' },
        { key: 'vat', label: 'VAT' },
        { key: 'sortCodeName', label: 'SortCode' },
        { key: 'debitAccount', label: 'DebitAccount' },
        { key: 'creditAccount', label: 'CreditAccount' },
        { key: 'violationReason', label: 'Error' },
    ];

    if (!open) return null;

    const reasonKeys = Object.keys(reasonCounts);
    const docTypeKeys = Object.keys(docTypeCounts);

    return (
        <Portal>
            <div
                className={`${modalStyles.overlay} ${isRtl ? modalStyles.rtl : modalStyles.ltr}`}
                onClick={onClose}
            >
                <div dir={direction} onClick={(e) => e.stopPropagation()}>
                    <div className={`${modalStyles.modal} ${modalStyles.modalEdit}`} role="dialog" aria-modal="true">
                        <div className={modalStyles.body}>
                            <div className={modalStyles.header}>
                                <Tooltip title={t('Close') || 'Close'}>
                                    <IconButton onClick={onClose} className={modalStyles.closeIcon}>
                                        <CancelIcon className={modalStyles.closeIcon} />
                                    </IconButton>
                                </Tooltip>
                                <div>{t(text.title)}</div>
                                {onRefresh ? (
                                    <Tooltip title={t('Refresh')}>
                                        <span>
                                            <IconButton onClick={handleRefresh} disabled={refreshing} className={modalStyles.closeIcon}>
                                                <RefreshIcon className={modalStyles.closeIcon} />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                ) : <div />}
                            </div>

                            {rows.length > 0 && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                        <TextField
                                            size="small"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            placeholder={t('SearchPalcodePlacement')}
                                            sx={{ minWidth: 320, flex: '0 1 420px', backgroundColor: '#fff' }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: search ? (
                                                    <InputAdornment position="end">
                                                        <IconButton size="small" onClick={() => setSearch('')}>
                                                            <ClearIcon fontSize="small" />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ) : null,
                                            }}
                                        />
                                        <span className={modalStyles.muted}>
                                            {t('ShownOfTotal', { shown: visibleRows.length, total: rows.length })}
                                        </span>
                                        {hasFilters ? (
                                            <button type="button" className={modalStyles.ghostBtn} onClick={clearFilters}>
                                                {t('ClearFilters')}
                                            </button>
                                        ) : null}
                                    </div>

                                    {reasonKeys.length > 1 && (
                                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                                            {filterChip('all', t('AllErrors'), rows.length, reasonFilter.length === 0, () => setReasonFilter([]))}
                                            {reasonKeys.map((reason) =>
                                                filterChip(reason, shortReason(t(reason)), reasonCounts[reason],
                                                    reasonFilter.includes(reason), () => toggleReason(reason), t(reason)))}
                                        </div>
                                    )}

                                    {docTypeKeys.length > 1 && (
                                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                                            {filterChip('all', t('AllTypes'), rows.length, docTypeFilter.length === 0, () => setDocTypeFilter([]))}
                                            {docTypeKeys.map((docType) =>
                                                filterChip(docType, t(docType), docTypeCounts[docType],
                                                    docTypeFilter.includes(docType), () => toggleDocType(docType)))}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className={modalStyles.scroll}>
                                {rows.length === 0 ? (
                                    <div className={modalStyles.emptyState}>
                                        {t(text.empty)}
                                    </div>
                                ) : (
                                    <div className={modalStyles.tableWrap}>
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table size="small" stickyHeader dir={direction}>
                                                <TableHead>
                                                    <TableRow>
                                                        {columns.map((col) => (
                                                            <TableCell
                                                                key={col.key}
                                                                align={textAlign}
                                                                sortDirection={sort.key === col.key ? sort.dir : false}
                                                            >
                                                                <TableSortLabel
                                                                    active={sort.key === col.key}
                                                                    direction={sort.key === col.key ? sort.dir : 'asc'}
                                                                    onClick={() => handleSort(col.key)}
                                                                >
                                                                    {t(col.label)}
                                                                </TableSortLabel>
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {visibleRows.length === 0 ? (
                                                        <TableRow>
                                                            <TableCell colSpan={columns.length} align="center" sx={{ color: '#999', py: 4 }}>
                                                                {t('NoResultsFound')}
                                                            </TableCell>
                                                        </TableRow>
                                                    ) : visibleRows.map((row, index) => (
                                                        <TableRow
                                                            key={`${row.documentId}-${index}`}
                                                            hover
                                                            sx={{ cursor: 'pointer' }}
                                                            onClick={() => handleRowClick(row)}
                                                        >
                                                            <TableCell align={textAlign}>{row.monthYear}</TableCell>
                                                            <TableCell align={textAlign}>{t(row.documentType)}</TableCell>
                                                            <TableCell align={textAlign}>{row.description || '—'}</TableCell>
                                                            <TableCell align={textAlign}>{row.reference || '—'}</TableCell>
                                                            <TableCell align={textAlign}>{beautifyNumber(row.amount)}</TableCell>
                                                            <TableCell
                                                                align={textAlign}
                                                                sx={{ color: Number(row.vat) !== 0 ? '#b71c1c' : undefined, whiteSpace: 'nowrap' }}
                                                            >
                                                                {vatLabel(row.vat, t)}
                                                            </TableCell>
                                                            <TableCell align={textAlign}>{row.sortCodeName || '—'}</TableCell>
                                                            <TableCell align={textAlign}>
                                                                <div>{accountLabel(row.debitAccountName, row.debitAccount) || '—'}</div>
                                                                {renderType(row.debitPalCodeType, row.debitMissingPalCode, row.debitPalCode)}
                                                            </TableCell>
                                                            <TableCell align={textAlign}>
                                                                <div>{accountLabel(row.creditAccountName, row.creditAccount) || '—'}</div>
                                                                {renderType(row.creditPalCodeType, row.creditMissingPalCode, row.creditPalCode)}
                                                            </TableCell>
                                                            <TableCell align={textAlign} sx={{ color: '#b71c1c' }}>
                                                                {t(row.violationReason)}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </div>
                                )}
                            </div>

                            <div className={modalStyles.footer}>
                                <button type="button" className={modalStyles.secondaryBtn} onClick={onClose}>
                                    {t('Close')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
};

export default PalcodePlacementCheckDialog;
